/**
 * Примерка: типы ответов `/api/pins/*` и `/api/fittings/*` и обёртки ручек
 * (панель референсов, карточка пина `/pins/:id`, экран `/generate`).
 *
 * Пара «пин × платье» → генерация с видом `fitting`; её строки ленты — `FitGen`
 * (обычная `Gen` плюс пин, платье и пресет). Запуск и пересъёмка платные:
 * ключ `key` — идемпотентность постановки (повторный клик не ставит вторую).
 *
 * Формы — по `reels/web/api_pins.py` и `reels/web/api_fittings.py`; даты — ISO без зоны.
 */
import { apiFetch, query } from "@/lib/api";
import type { Gen } from "@/lib/project";
import { post, type Pin } from "@/lib/trends";
import type { PicRole } from "@/lib/uploads";
import type { EnhanceOutcome } from "@/lib/verdict";

/** Больше кадров (пар × n) за один запуск сервис не берёт. */
export const MAX_IMAGES = 40;
/** Входов в одной паре (сцена + вставленные + фото платья) — потолок сервиса. */
export const MAX_INPUTS = 16;
/** Вставленных картинок на запуск — потолок сервиса. */
export const EXTRAS_MAX = 10;
/** Пар для панели референсов: пины × 1 платье × черновик n=2. */
export const MAX_PAIRS = MAX_IMAGES / 2;

export type Preset = { label: string; note: string; model: string; quality: string; size: string; n: number };

/** Правило каталога; `drop_portrait` — сервис снимет его, когда во входах есть внешность модели (№317). */
export type Rule = { code: string; label: string; text: string; drop_portrait: boolean };

/** Модель селектора: OpenAI — прямой вызов, OpenRouter — через `imagegen` (лимиты из discovery). */
export type ModelOption = {
  code: string;
  label: string;
  provider: "openai" | "openrouter";
  /** цена кадра из таблицы пробы (только OpenRouter) */
  frame_price: number | null;
  max_inputs: number;
  max_n: number;
  aspects: string[];
  /** `null` — у модели нет выбора качества */
  quality: string[] | null;
  resolution: string[] | null;
  default_params: Record<string, string | number>;
  /** причина, по которой модель недоступна */
  disabled?: string;
};

/** Модель по умолчанию: пресеты, размеры WxH и пересъёмка — только у неё. */
export const DEFAULT_MODEL = "gpt-image-2";

/** Модель OpenRouter (код вида `вендор/модель`): пересъёмку сервис для неё отказывает. */
export const isRouterModel = (code: string | null | undefined): boolean => !!code && code.includes("/");

export type FittingOptions = {
  presets: Record<string, Preset>;
  default_preset: string;
  qualities: [string, string][];
  sizes: string[];
  n_max: number;
  fit_px: number[];
  fit_px_default: number;
  rules: { fitting: Rule[]; reference: Rule[]; manual: Rule[] };
  models: ModelOption[];
  max_images: number;
  note_max: number;
  prompt_max: number;
};

/** Параметры запуска; `null` — решает сервис (пресет, весь каталог правил, сборка промпта). */
export type GenParams = {
  preset: string;
  quality: string | null;
  size: string | null;
  n: number | null;
  fit_px: number;
  rules_fitting: string[] | null;
  rules_reference: string[] | null;
  rules_manual: string[] | null;
  prompt: string | null;
  /** `null` — модель пресета (gpt-image-2) */
  model: string | null;
  /** только OpenRouter: `auto` — по сцене либо значение из `aspects` */
  aspect: string | null;
  resolution: string | null;
};

export type RefPick = { id: number; note: string };

export type FitReference = {
  id: number;
  library_id: number;
  library_name: string;
  category_name: string;
  note: string | null;
  asset_path: string;
  width: number | null;
  height: number | null;
};

export type FitParams = {
  quality: string;
  size: string;
  size_mode: "auto" | "fixed";
  n: number;
  fit_px: number;
  rules: string[];
  /** У старых снимков ключа нет. */
  photo_ids: number[] | null;
  preset: string | null;
  prompt_edited: boolean;
  /** «Мой» промпт запуска — без дописанных сервисом «Что учесть» и списка входов; у авто — null. */
  prompt_mode: "auto" | "custom";
  prompt_custom: string | null;
  /** У старых снимков ключей модели нет (тогда — gpt-image-2). */
  model: string | null;
  provider: "openai" | "openrouter" | null;
  aspect_ratio: string | null;
  resolution: string | null;
};

/**
 * Вставленная картинка в строке ленты: превью по `path`, комментарий — подсказка модели, роль —
 * чем она была в запуске (у снимков до 15.10 ключа нет, значит `hint`).
 */
export type FeedExtra = { asset_id: number; path: string; comment: string; role?: PicRole };

export type FitGen = Gen & {
  pin_id: number | null;
  own_dress_id: number | null;
  pin: { id: number; code: string; title: string | null } | null;
  /** `id: null, uploaded: true` — платье из загрузок (папки нет). */
  dress: { id: number | null; name?: string | null; uploaded?: boolean } | null;
  preset: string | null;
  /** `upload` — загруженное, `enhance` — улучшение Krea: их не повторяют и не пересъёмывают. */
  kind: "shot" | "fitting" | "reference" | "manual" | "upload" | "enhance";
  extras: FeedExtra[];
  /** Загрузки фото платья этого запуска (пути — в `inputs` по `asset_id`). */
  uploads: { asset_id: number; comment: string }[];
  reference_item_id: number | null;
  reference: FitReference | null;
  note: string | null;
  prompt: string | null;
  params: FitParams | null;
  /** Отличия от пресета — фишки в шапке группы. */
  diff: string[];
};

export type FittingsResult = { gens: FitGen[]; pending: number; error?: string };

export type FeedFilter = { pins?: number[]; refs?: number[]; dress?: number; generation?: number; manual?: boolean };

/** Картинка в теле запуска; `role` пишем только когда она не умолчание (`hint`). */
export type PicPick = { asset_id: number; comment: string; role?: PicRole };

/** Платье запуска: папка (`id`, фото папки и/или загрузки) либо только загрузки (`id: null`). */
export type DressPick = { id: number | null; photo_ids: number[]; uploads: PicPick[] };

export type EstimateBody = {
  pins: number[];
  refs: RefPick[];
  extras: PicPick[];
  /** «Что учесть» запуска без сцены (у референса заметка своя). */
  note: string;
  dresses: DressPick[];
  params: GenParams;
};

/** Чья сцена в паре: пин, референс либо `manual` (сцены нет). */
type PairOwner = { pin_id?: number; ref_id?: number; manual?: boolean };

export type EstimateResult = {
  pairs: (PairOwner & {
    dress_id: number | null;
    dress_key: number | string;
    inputs: number;
    model: string;
    /** WxH у OpenAI, `null` у OpenRouter */
    size: string | null;
    /** только OpenRouter */
    aspect_ratio?: string;
    cost: number;
    basis: "facts" | "formula" | "table";
  })[];
  total: number;
  images: number;
};

export type LaunchResult = {
  generations: (PairOwner & { dress_id: number | null; dress_key: number | string; generation_id: number; existing: boolean })[];
  errors: string[];
  total_estimate: number;
};

export type PinCard = Pin & {
  scene_note: string | null;
  description: string | null;
  has_image_url: boolean;
  video_ref_id: number | null;
  found_in: {
    topics: { topic_id: number; topic_name: string; query_id: number; query_name: string }[];
    dresses: { dress_id: number; dress_name: string | null }[];
  };
  fittings: FittingsResult;
};

export type PinLite = Pin & { scene_note: string | null; has_image_url: boolean };

export const fittingKeys = {
  pin: (id: number) => ["pin", id] as const,
  pins: (ids: number[]) => ["pins", ids.join(",")] as const,
  feed: (f: FeedFilter) => ["fittings", f] as const,
  options: ["fittings", "options"] as const,
};

/** Префикс всех запросов примерки: по нему кэш инвалидируется целиком. */
export const FITTINGS_PREFIX = ["fittings"] as const;

export const loadPin = (id: number) => apiFetch<PinCard>(`/api/pins/${id}`);
export const loadPins = (ids: number[]) =>
  apiFetch<{ pins: PinLite[] }>("/api/pins" + query({ ids: ids.join(",") }));
export const saveSceneNote = (id: number, text: string) =>
  post<{ scene_note: string | null }>(`/api/pins/${id}/scene-note`, { text });

export const loadFittingOptions = () => apiFetch<FittingOptions>("/api/fittings/options");
export const estimateFittings = (body: EstimateBody) => post<EstimateResult>("/api/fittings/estimate", body);
export const launchFittings = (body: EstimateBody & { key: string }) => post<LaunchResult>("/api/fittings", body);
export const loadFittings = (f: FeedFilter) =>
  apiFetch<FittingsResult>(
    "/api/fittings" + query({ pin: f.pins?.map(String), ref: f.refs?.map(String), dress: f.dress, generation: f.generation, manual: f.manual }),
  );
export const pickFittingVariant = (variantId: number) =>
  post<FittingsResult & { enhance: EnhanceOutcome }>(`/api/fittings/variants/${variantId}/pick`, {});
export const reshootFittingVariant = (variantId: number, key: string) =>
  post<FittingsResult>(`/api/fittings/variants/${variantId}/reshoot`, { key });

/**
 * Предпросмотр промпта: заметку сцены присылает вызывающий (для пина — `scene_note`). `products` — по записи
 * на каждое фото первой пары (комментарий загрузки или `null` у фото папки), `extras` — комментарии вставленных.
 * Ни `pin`, ни `ref` — промпт запуска без сцены. `prompt` — «Мой»: в ответе `tail` — что сервис допишет к нему;
 * `base` — задание и правила вида без заметки и списка входов (заготовка «Моего»).
 */
export const buildPrompt = (body: {
  pin?: number;
  ref?: number;
  note: string;
  products: (string | null)[];
  /** Строка — картинка без роли (подсказка), объект — с ролью: предпросмотр обязан совпасть с запуском. */
  extras: (string | { comment: string; role: PicRole })[];
  rules: string[] | null;
  prompt?: string;
}) =>
  post<{
    prompt: string;
    tail: string;
    base: string;
    /** заготовка «Моего» с разбором сцены (для одной сцены) */
    scene_base: string;
    /** разбор сцены первой пары: есть / ещё нет / у пары его не бывает */
    scene: "ready" | "missing" | null;
    scene_estimate: number | null;
  }>("/api/fittings/prompt", body);

/** Разбор сцены референса по кнопке; `existing` — он уже был, деньги не потрачены. */
export const analyzeScene = (ref: number) =>
  post<{ lines: number; cost: number; existing: boolean }>("/api/fittings/scene", { ref });

/** Адрес экрана генерации с набором сцен (пины, референсы), платьем и прежним запуском (`from`). */
export const fittingPath = (scenes: { pins?: number[]; refs?: number[] }, dressId?: number, from?: number): string =>
  "/generate" + query({ pin: scenes.pins?.map(String), ref: scenes.refs?.map(String), dress: dressId, from });
